/* eslint-disable no-continue */
import { createSelector } from '@reduxjs/toolkit';

import { defaultSorterTypes, HabitatSortType, sortHabitats } from '@/helpers/habitatsSorter';
import { HabitatListItem } from '@/queries/habitats/types';

import { AppState } from '../helper';
import { selectHasExplorerFeature } from './users';

export const selectAllFreemiumHabitatsMap = createSelector(
  (state: AppState) => state.user.logged,
  selectHasExplorerFeature,
  (state: AppState) => state.user.subscription.habitatsAccessUntil,
  (state: AppState) => state.allHabitats,
  (logged, hasExplorerFeature, habitatsAccessUntil, allHabitats) => {
    return allHabitats.reduce(
      (acc, habitat) => {
        if (!logged) {
          acc[habitat._id] = true;
        } else if (
          hasExplorerFeature ||
          !!(habitatsAccessUntil?.[habitat._id] && Date.parse(habitatsAccessUntil[habitat._id]) > Date.now())
        ) {
          acc[habitat._id] = true;
        } else {
          acc[habitat._id] = false;
        }
        return acc;
      },
      {} as Record<string, boolean>,
    );
  },
);

/** @deprecated */
export const selectAllPremiumHabitatsAsPaidUserMap = createSelector(
  (state: AppState) => state.user.logged,
  selectHasExplorerFeature,
  (state: AppState) => state.allHabitats,
  (logged, canAccessAll, allHabitats) => {
    const map: Record<string, boolean> = {};
    allHabitats.forEach((habitat) => {
      if (!logged) {
        map[habitat._id] = false;
      } else {
        map[habitat._id] = canAccessAll;
      }
    });
    return map;
  },
);

export const createSortedHabitatsSelector = (
  sorterTypes: readonly HabitatSortType<HabitatListItem>[] = defaultSorterTypes,
) =>
  createSelector(
    (state: AppState) => state.allHabitats,
    selectHasExplorerFeature,
    (state: AppState) => state.remoteConfig.lastMediaCaptureThresholdForFeaturedHabitatMinutes,
    (state: AppState) => state.user.subscription.habitatsAccessUntil,
    (allHabitats, hasExplorerFeature, threshold, habitatsAccessUntil) => {
      const sorted = sortHabitats(allHabitats, !hasExplorerFeature, sorterTypes, threshold);
      return sorted.map((habitat) => ({
        ...habitat,
        isLocked:
          !hasExplorerFeature &&
          !(habitatsAccessUntil?.[habitat._id] && Date.parse(habitatsAccessUntil[habitat._id]) > Date.now()),
      }));
    },
  );
